/**
 * What the project-file upload endpoint accepts, mirroring
 * `projects/project_file_storage.py`. Checked before uploading.
 */

import type { EpiStagedAttachment } from '../types/epi-chat-types';

/** Extensions the platform can read. An upload of anything else is refused. */
export const ACCEPTED_ATTACHMENT_EXTENSIONS = ['.csv', '.xlsx', '.xls'] as const;

/** The `accept` attribute for a file picker restricted to the above. */
export const ATTACHMENT_ACCEPT_ATTR = ACCEPTED_ATTACHMENT_EXTENSIONS.join(',');

/** Upload size ceiling, as the researcher is told it. Matches `MAX_FILE_MB` on the endpoint. */
export const MAX_ATTACHMENT_MB = 10;

/** Matches `MAX_FILE_BYTES` on the endpoint — under the ingress body limit, framing included. */
export const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024 - 8 * 1024;

/** One file per message. `MAX_ATTACHMENTS_PER_MESSAGE` is the wire cap above it. */
export const MAX_STAGED_ATTACHMENTS = 1;

export interface AttachmentGate {
  /** Any staged upload still in flight. Sending now would drop it from its message. */
  uploading: boolean;
  /** At least one reference is ready to ride a message. */
  hasReady: boolean;
  /** This message already holds its one file. */
  atLimit: boolean;
}

/** What staged attachments allow right now — one rule for every composer. */
export const attachmentGate = (staged?: readonly EpiStagedAttachment[]): AttachmentGate => {
  const entries = staged ?? [];
  return {
    uploading: entries.some((entry) => entry.status === 'uploading'),
    hasReady: entries.some((entry) => entry.status === 'ready'),
    atLimit: entries.length >= MAX_STAGED_ATTACHMENTS,
  };
};

/**
 * Whether this composer may send right now — one rule for every composer.
 *
 * A ready file is a message on its own, so text is not required. Sending
 * mid-upload would drop the file from its own message; a failed upload does
 * not block, the researcher removes it and sends without it.
 */
export const canSendMessage = (args: {
  text: string;
  attachments?: readonly EpiStagedAttachment[];
}): boolean => {
  const { uploading, hasReady } = attachmentGate(args.attachments);
  if (uploading) return false;
  return Boolean(args.text.trim()) || hasReady;
};

/** The attach button's tooltip, given what the host wired and what is staged. */
export const attachTooltip = (args: {
  atLimit: boolean;
  canAttach: boolean;
  accept: string;
}): string => {
  if (args.atLimit) {
    return 'A message can carry one file. Remove it to attach another.';
  }
  if (!args.canAttach) return 'Attach a file (coming soon)';
  return `Attach a file (${args.accept.split(',').join(', ')})`;
};

/** Researcher-facing reason this file can't be attached, or `null` when it can. */
export const attachmentRejectionReason = (file: File): string | null => {
  const lower = file.name.toLowerCase();
  if (!ACCEPTED_ATTACHMENT_EXTENSIONS.some((extension) => lower.endsWith(extension))) {
    return `Only ${ACCEPTED_ATTACHMENT_EXTENSIONS.join(', ')} files are supported.`;
  }
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `That file is larger than ${MAX_ATTACHMENT_MB} MB.`;
  }
  return null;
};
